import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LogOut, User, Zap, ExternalLink, ChevronDown, Building2, MapPin, Trophy, Calendar, Users, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useArenaxCapabilities } from '@/lib/arenaxCapabilities';
import { trackMenuNavigate } from '@/lib/analytics';
import {
  BulletproofMenu,
  BulletproofMenuTrigger,
  BulletproofMenuContent,
  DropdownItem,
  DropdownSectionLabel,
} from '@/components/ui/bulletproof-menu';
import { useArenaxTheme, ArenaxThemeToggle } from './useArenaxTheme';
import { ArenaxWordmark } from './ArenaxWordmark';
import { usePwaInstall } from '@/lib/pwaInstall';
import { toast } from 'sonner';

// Phase 117 — dual-theme cinematic+editorial shell wrapping every public +
// player ArenaX page.  Dark mode keeps the original DAZN/Bleacher-Report
// canvas; light mode swaps to the warm-editorial palette (The Athletic /
// F1 magazine).  All surface colors come from the CSS variables defined
// in `arenax-theme.css`.  The 🌙/☀️ toggle is wired into the top-right
// of the nav and persists per-device via localStorage.
// Phase 124 — adds the "Become a Host" CTA + reads the operator activation
// state so the CTA only shows when at least one capability is unactivated.
export default function ArenaxLayout({ children, hideNav = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPlayer = user?.role === 'player';
  const isFederated = user && user.role && user.role !== 'player';
  const [theme, toggleTheme] = useArenaxTheme();
  const [opStatus, setOpStatus] = useState(null);
  const { canInstall, installed, isStandalone, isIos, promptInstall } = usePwaInstall();
  const installable = canInstall && !installed && !isStandalone;
  const iosNudge = isIos && !isStandalone && !installed;
  const handleWordmarkClick = async (e) => {
    // Phase 117B — intercept brand click to fire the cached PWA install
    // prompt when available.  Falls through to /arenax routing otherwise.
    if (installable) {
      e.preventDefault();
      const accepted = await promptInstall();
      if (accepted) return;
      navigate('/arenax');
      return;
    }
    if (iosNudge) {
      e.preventDefault();
      toast.message('Tap Share → "Add to Home Screen"', {
        description: 'Install ArenaX on your iPhone home screen',
        duration: 5000,
      });
      navigate('/arenax');
      return;
    }
    // Default <Link> behavior carries on.
  };
  const wordmarkTitle = installable
    ? '✨ Click logo to install ArenaX on your home screen!'
    : iosNudge
      ? 'Add ArenaX to your iPhone via Share → Add to Home Screen'
      : 'Open ArenaX';

  useEffect(() => {
    if (!user) { setOpStatus(null); return; }
    let stopped = false;
    api.get('/arenax/me/operator-status')
      .then(r => { if (!stopped) setOpStatus(r.data); })
      .catch(() => {});
    return () => { stopped = true; };
  }, [user]);
  // CEO doctrine: surface the activation pathway to ANY user who hasn't
  // yet activated all three capabilities — public visitors included
  // (they'll be sent through auth on click).  Once a user has at least
  // one capability active, the CTA becomes a quiet "Host" badge they can
  // tap to add more.
  // Phase 125 — dynamic role-aware navbar CTA:
  //   Non-operator   → "Become a Host" (gradient)
  //   Venue owner    → "Manage Venue"  (quiet badge)
  //   Organizer      → "Run Tournament" (quiet badge)
  //   Academy        → "Manage Academy" (quiet badge)
  //   Multi-capability users see the most recently activated capability;
  //   they can switch via the "Host on ArenaX" avatar dropdown section.
  const needsActivation = !opStatus || opStatus.needs_activation;
  const showHostCTA = !user || needsActivation;
  const showHostBadge = !!opStatus && !opStatus.needs_activation;
  const operatorCTA = (() => {
    if (!opStatus) return { label: 'Host', href: '/arenax/host' };
    // Priority: academy > venue > organizer.  Academy is the deepest
    // ownership relationship (academy implies venue), so an
    // academy_owner sees "Manage Academy" even if they ALSO have an
    // organizer/venue activation.  Switch among capabilities via the
    // "Host on ArenaX" avatar dropdown section.
    if (opStatus.has_academy) return { label: 'Manage Academy', href: '/arenax/host/academy/manage' };
    if (opStatus.has_venue) return { label: 'Manage Venue', href: '/arenax/host/venue/manage' };
    if (opStatus.has_organizer) return { label: 'Run Tournament', href: '/arenax/host/organizer/manage' };
    return { label: 'Host', href: '/arenax/host' };
  })();

  return (
    <div
      className="min-h-screen arenax-themed"
      style={{
        background: 'var(--ax-bg-gradient)',
        color: `hsl(var(--ax-text))`,
      }}
    >
      {!hideNav && (
        <header
          className="sticky top-0 z-40 backdrop-blur-xl border-b"
          style={{
            background: 'var(--ax-nav-gradient)',
            borderColor: 'var(--ax-border-quiet)',
          }}
          data-testid="arenax-header"
        >
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-between">
            <Link
              to="/arenax"
              onClick={handleWordmarkClick}
              title={wordmarkTitle}
              aria-label={wordmarkTitle}
              className={`flex items-center gap-2 group relative ${installable ? 'cursor-pointer' : ''}`}
              data-testid="arenax-brand"
            >
              <ArenaxWordmark size="md" withAttribution />
              {installable && (
                <span
                  className="absolute -bottom-3 left-0 text-[9px] font-bold tracking-wider uppercase opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap"
                  style={{ color: 'hsl(var(--ax-accent))' }}
                  data-testid="arenax-pwa-install-hint"
                >
                  ✨ Tap to install app
                </span>
              )}
            </Link>

            <nav className="hidden md:flex items-center gap-1">
              <ArenaxNavLink to="/arenax" active={location.pathname === '/arenax'} testid="arenax-nav-home">Home</ArenaxNavLink>
              <ArenaxNavLink to="/arenax/games" active={location.pathname.startsWith('/arenax/games')} testid="arenax-nav-games">Games</ArenaxNavLink>
              <ArenaxNavLink to="/arenax/squads" active={location.pathname.startsWith('/arenax/squads') || location.pathname.startsWith('/arenax/join-squad')} testid="arenax-nav-squads">Squads</ArenaxNavLink>
              <ArenaxNavLink to="/arenax/tournaments" active={location.pathname.startsWith('/arenax/tournaments')} testid="arenax-nav-tournaments">Leagues</ArenaxNavLink>
              <ArenaxNavLink to="/arenax/seasons" active={location.pathname.startsWith('/arenax/seasons')} testid="arenax-nav-seasons">Seasons</ArenaxNavLink>
              <ArenaxNavLink to="/arenax/city" active={location.pathname.startsWith('/arenax/city')} testid="arenax-nav-city">City</ArenaxNavLink>
              <ArenaxNavLink to="/arenax/stories" active={location.pathname.startsWith('/arenax/stories')} testid="arenax-nav-stories">Stories</ArenaxNavLink>
              <ArenaxNavLink to="/arenax/rankings" active={location.pathname.startsWith('/arenax/rankings')} testid="arenax-nav-rankings">Rankings</ArenaxNavLink>
              <ArenaxNavLink to="/arenax/venues" active={location.pathname.startsWith('/arenax/venues')} testid="arenax-nav-venues">Venues</ArenaxNavLink>
              {isPlayer && <ArenaxNavLink to="/arenax/player" active={location.pathname === '/arenax/player'} testid="arenax-nav-dashboard">My Hub</ArenaxNavLink>}
            </nav>

            <div className="flex items-center gap-2">
              {showHostCTA && (
                <Link
                  to="/arenax/host"
                  data-testid="arenax-become-host-cta"
                  className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg transition-all hover:-translate-y-0.5"
                  style={{
                    background: 'var(--ax-primary-gradient)',
                    color: `hsl(var(--ax-button-text))`,
                    boxShadow: 'var(--ax-shadow-glow)',
                  }}
                >
                  <Building2 className="h-3.5 w-3.5" />
                  Become a Host
                </Link>
              )}
              {showHostBadge && (
                <Link
                  to={operatorCTA.href}
                  data-testid="arenax-host-badge"
                  className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border transition-colors"
                  style={{
                    color: `hsl(var(--ax-text-muted))`,
                    borderColor: 'var(--ax-border-quiet)',
                    background: 'var(--ax-card-bg)',
                  }}
                  title="Manage your operator capabilities"
                >
                  <Building2 className="h-3 w-3" />
                  {operatorCTA.label}
                </Link>
              )}
              <ArenaxThemeToggle theme={theme} onToggle={toggleTheme} />
              {!user && (
                <>
                  <Link to="/arenax/auth/login" data-testid="arenax-cta-login">
                    <button
                      className="px-4 py-2 text-xs font-semibold transition-colors"
                      style={{ color: `hsl(var(--ax-text-muted))` }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = `hsl(var(--ax-accent-cyan))`; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = `hsl(var(--ax-text-muted))`; }}
                    >
                      Sign in
                    </button>
                  </Link>
                  <Link to="/arenax/auth/register" data-testid="arenax-cta-register">
                    <button
                      className="px-4 py-2 text-xs font-bold rounded-lg shadow-lg transition-all hover:-translate-y-0.5"
                      style={{
                        background: 'var(--ax-primary-gradient)',
                        color: `hsl(var(--ax-button-text))`,
                        boxShadow: 'var(--ax-shadow-glow)',
                      }}
                    >
                      Get Started
                    </button>
                  </Link>
                </>
              )}
              {isPlayer && (
                <ArenaxAvatarMenu
                  user={user}
                  isFederated={false}
                  onLogout={() => { logout(); navigate('/arenax'); }}
                  onProfile={() => navigate('/arenax/player')}
                />
              )}
              {isFederated && (
                <ArenaxAvatarMenu
                  user={user}
                  isFederated={true}
                  onLogout={() => { logout(); navigate('/arenax'); }}
                  onProfile={() => navigate('/arenax')}
                />
              )}
            </div>
          </div>
        </header>
      )}
      <main>{children}</main>
      <footer className="border-t mt-20 py-8 px-5" style={{ borderColor: 'var(--ax-border-quiet)' }}>
        <div
          className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs"
          style={{ color: `hsl(var(--ax-text-faint))` }}
        >
          <div className="flex items-center gap-2">
            <Zap className="h-3.5 w-3.5" style={{ color: `hsl(var(--ax-accent-cyan))` }} />
            <span>
              <ArenaxWordmark size="xs" /> is a premium module of{' '}
              <Link to="/" style={{ color: `hsl(var(--ax-accent-cyan))` }}>Out-Play.in</Link>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:underline">Privacy</Link>
            <Link to="/terms" className="hover:underline">Terms</Link>
            <span>© 2026 Out-Play Labs</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

/**
 * Phase 118 — Unified avatar dropdown.
 * Phase 178.5 — Operator Continuity hardening (single-source capability truth).
 * Phase 178.5.C — Bulletproof click handling (onMouseDown + useNavigate).
 * Phase 178.5.D — Migrated to platform reliability primitive
 *                 `<BulletproofMenu>` from `@/components/ui/bulletproof-menu`.
 *
 * The menu itself is now a thin orchestrator: it decides WHICH items to
 * render based on hydrated capabilities, the primitive handles outside-click,
 * Esc, keyboard navigation, click reliability, accessibility, and analytics
 * routing.
 *
 * Three structural groups:
 *   PUBLIC          — ArenaX Home / My Bookings / My Squads
 *   HOST ON ARENAX  — capability-driven (Manage Academy / Venue / Run Tournament)
 *                     Falls back to "List / Register / Become" CTAs for missing caps.
 *   PLATFORM        — Out-Play (federated only) + Sign out
 */
function ArenaxAvatarMenu({ user, isFederated, onLogout, onProfile }) {
  const { hasCap, refresh: refreshCaps, loading: capsLoading } = useArenaxCapabilities();
  const initials = (user?.name || 'U').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
  const roleLabel = isFederated
    ? (user?.role || 'member').replace('_', ' ')
    : 'player';

  const canManageAcademy = hasCap('manage_academy');
  const canManageVenue   = hasCap('publish_venue');
  const canManageOrganizer = hasCap('host_tournament') || hasCap('create_league');

  const hostItems = [
    canManageAcademy
      ? { key: 'academy-manage', label: 'Manage Academy', href: '/arenax/host/academy/manage', icon: Building2, activated: true }
      : { key: 'academy-register', label: 'Register Academy', href: '/arenax/host/academy', icon: Building2, activated: false },
    canManageVenue
      ? { key: 'venue-manage', label: 'Manage Venue', href: '/arenax/host/venue/manage', icon: MapPin, activated: true }
      : { key: 'venue-register', label: 'List Your Venue', href: '/arenax/host/venue', icon: MapPin, activated: false },
    canManageOrganizer
      ? { key: 'organizer-manage', label: 'Run Tournament', href: '/arenax/host/organizer/manage', icon: Trophy, activated: true }
      : { key: 'organizer-register', label: 'Become Organizer', href: '/arenax/host/organizer', icon: Trophy, activated: false },
  ];

  // Analytics surface — every dropdown navigation goes through the central
  // `track()` pipe (Phase 178.6).  Buffers locally + forwards to PostHog/
  // Mixpanel via `window.__OUTPLAY_TRACK__` if installed.
  const onNavigate = (payload) => {
    trackMenuNavigate({
      source: payload?.source || 'arenax_avatar',
      destination: payload?.destination,
      capability_context: payload?.capability_context,
    });
  };

  // Pull fresh caps on each open so a freshly-completed onboarding wizard
  // can't surface a stale "Register Academy" CTA.
  const handleOpen = () => {
    try { refreshCaps(); } catch (_) { /* non-fatal */ }
  };

  const capabilityContext = {
    federated: isFederated,
    caps: { manage_academy: canManageAcademy, publish_venue: canManageVenue, host_tournament: canManageOrganizer },
  };

  return (
    <BulletproofMenu onNavigate={onNavigate} capabilityContext={capabilityContext}>
      <BulletproofMenuTrigger asChild onOpenCapture={handleOpen}>
        <button
          type="button"
          className="h-9 pl-1 pr-2 rounded-full border flex items-center gap-1.5 transition-colors"
          style={{
            background: 'var(--ax-card-bg)',
            borderColor: 'var(--ax-border-bold)',
            color: 'hsl(var(--ax-text))',
          }}
          data-testid="arenax-avatar"
        >
          <span
            className="h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-extrabold"
            style={{
              background: 'var(--ax-primary-gradient)',
              color: 'hsl(var(--ax-button-text))',
            }}
          >
            {initials}
          </span>
          <ChevronDown className="h-3 w-3" style={{ color: 'hsl(var(--ax-text-faint))' }} />
        </button>
      </BulletproofMenuTrigger>
      <BulletproofMenuContent
        className="absolute right-0 mt-2 w-64 rounded-xl border shadow-xl overflow-hidden z-50"
        style={{
          background: 'var(--ax-card-bg-soft)',
          borderColor: 'var(--ax-border-bold)',
          backdropFilter: 'blur(12px)',
        }}
        data-testid="arenax-avatar-menu"
      >
        {/* Identity header */}
        <div
          className="px-4 py-3 border-b"
          style={{ borderColor: 'var(--ax-divider)' }}
        >
          <div className="text-sm font-semibold truncate" style={{ color: 'hsl(var(--ax-text))' }}>
            {user?.name || 'ArenaX member'}
          </div>
          <div
            className="text-[10px] tracking-[0.25em] uppercase font-bold mt-0.5"
            style={{ color: 'hsl(var(--ax-accent-cyan))' }}
          >
            {roleLabel}
          </div>
        </div>

        {/* ── PUBLIC group ───────────────────────────────────────── */}
        <DropdownSectionLabel testid="arenax-avatar-section-public">Public</DropdownSectionLabel>
        <DropdownItem
          onSelect={onProfile}
          icon={User}
          testid="arenax-avatar-profile"
          source="arenax_avatar_public"
        >
          {isFederated ? 'ArenaX home' : 'My player hub'}
        </DropdownItem>
        <DropdownItem
          href="/arenax/me/bookings"
          icon={Calendar}
          iconAccent
          testid="arenax-avatar-my-bookings"
          source="arenax_avatar_public"
        >
          My bookings
        </DropdownItem>
        <DropdownItem
          href="/arenax/squads"
          icon={Users}
          iconAccent
          testid="arenax-avatar-my-squads"
          source="arenax_avatar_public"
        >
          My squads
        </DropdownItem>

        {/* ── HOST ON ARENAX group (capability-driven) ──────────── */}
        <DropdownSectionLabel testid="arenax-avatar-host-section">Host on ArenaX</DropdownSectionLabel>
        {capsLoading ? (
          <div
            className="px-4 py-2 text-[10px]"
            style={{ color: 'hsl(var(--ax-text-faint))' }}
            data-testid="arenax-avatar-host-loading"
          >
            Loading host options…
          </div>
        ) : (
          hostItems.map((item) => {
            const Icon = item.activated ? item.icon : Plus;
            return (
              <DropdownItem
                key={item.key}
                href={item.href}
                icon={Icon}
                iconColor={item.activated ? '#10b981' : 'hsl(var(--ax-accent-cyan))'}
                testid={`arenax-avatar-host-${item.key}`}
                source="arenax_avatar_host"
                trailing={item.activated ? (
                  <span
                    className="text-[9px] uppercase tracking-wider font-bold"
                    style={{ color: '#10b981' }}
                  >
                    Live
                  </span>
                ) : null}
              >
                {item.label}
              </DropdownItem>
            );
          })
        )}

        {/* ── PLATFORM group ────────────────────────────────────── */}
        {isFederated && (
          <>
            <DropdownSectionLabel testid="arenax-avatar-section-platform">Platform</DropdownSectionLabel>
            <DropdownItem
              href="/dashboard"
              icon={ExternalLink}
              iconAccent
              testid="arenax-back-outplay"
              source="arenax_avatar_platform"
              trailing={
                <span
                  className="text-[9px] uppercase tracking-wider"
                  style={{ color: 'hsl(var(--ax-text-faint))' }}
                >
                  {(user?.role || '').replace('_', ' ')}
                </span>
              }
            >
              Out-Play
            </DropdownItem>
          </>
        )}

        <DropdownItem
          onSelect={onLogout}
          icon={LogOut}
          testid="arenax-logout"
          source="arenax_avatar_logout"
          destructive
        >
          Sign out
        </DropdownItem>
      </BulletproofMenuContent>
    </BulletproofMenu>
  );
}

function ArenaxNavLink({ to, active, children, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="relative px-4 py-2 text-xs font-semibold tracking-wide rounded-lg transition-all"
      style={{
        color: active ? `hsl(var(--ax-accent-cyan))` : `hsl(var(--ax-text-muted))`,
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = `hsl(var(--ax-text))`; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = `hsl(var(--ax-text-muted))`; }}
    >
      {children}
      {active && (
        <span
          className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-8 h-[2px] rounded-full"
          style={{ background: 'var(--ax-primary-gradient)' }}
        />
      )}
    </Link>
  );
}
